import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import supabase from "../supabase";
import { useRenovCredits } from "../context/RenovCreditsContext";
import { useNotification } from "../context/NotificationContext";

export default function useRoomData() {
  const { id } = useParams();
  const [room, setRoom] = useState(null);
  const [images, setImages] = useState([]);
  const [defaultImageIndex, setDefaultImageIndex] = useState(0);
  const [name, setName] = useState("");
  const [superficie, setSuperficie] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userPlan, setUserPlan] = useState("Free");
  const [analyses, setAnalyses] = useState([]); // New state for analyses

  const { setRenovCredits } = useRenovCredits();
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchRoomDetails = async () => {
      try {
        const { data: roomData, error: roomError } = await supabase
          .from("rooms")
          .select("*, properties(*)") // Removed analyse_json and analysis_type
          .eq("id", id)
          .single();

        if (roomError) {
          console.error("Error fetching room data:", roomError.message);
          setLoading(false);
          return;
        }

        const { data: imagesData, error: imagesError } = await supabase
          .from("images")
          .select("*")
          .eq("room_id", id);

        if (imagesError) {
          console.error("Error fetching images data:", imagesError.message);
          // Continue even if images fail, as room data might still be useful
        }

        // Fetch all analyses for the room
        const { data: analysesData, error: analysesError } = await supabase
          .from("room_analyses")
          .select("*")
          .eq("room_id", id)
          .order("created_at", { ascending: false });

        if (analysesError) {
          console.error("Error fetching analyses data:", analysesError.message);
          // Continue even if analyses fail
        }

        setAnalyses(analysesData || []);

        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) {
          console.error("Error fetching user data:", userError.message);
          // Continue even if user data fails
        }

        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("renovcredits, plan")
          .eq("id", user?.id)
          .single();

        if (profileError) {
          console.error("Error fetching profile data:", profileError.message);
          // Continue even if profile data fails
        }

        // Initialize localisation_projet before setting room state
        let localisation_projet = "Non spécifiée";
        if (roomData?.properties?.departement_id) {
          const { data: deptData, error: deptError } = await supabase
            .from("departements")
            .select("nom")
            .eq("id", roomData.properties.departement_id)
            .single();

          if (!deptError && deptData) {
            localisation_projet = deptData.nom;
          } else if (deptError) {
            console.error("Error fetching department data:", deptError.message);
          }
        }

        setRoom({
          ...roomData,
          property_localisation: localisation_projet,
          // Correctly access properties from roomData.properties
          surface_habitable_totale_logement_m2: roomData?.properties?.surface_habitable_totale_logement_m2 || 0,
          age_logement_annees: roomData?.properties?.age_logement_annees || 0,
        });

        setImages(
          (imagesData || []).map((img) => ({
            ...img,
            isNew: false,
          }))
        );

        setName(roomData?.name || "");
        setSuperficie(roomData?.superficie || "");
        setRenovCredits(profileData?.renovcredits || 0);
        setUserPlan(profileData?.plan || "Free");
      } catch (error) {
        console.error("Failed to fetch room details:", error.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRoomDetails();
    }

    // Cleanup function to avoid memory leaks if the component unmounts
    return () => {
      setRoom(null);
      setImages([]);
      setDefaultImageIndex(0);
      setName("");
      setSuperficie("");
      setIsEditing(false);
      setLoading(true);
      setUserPlan("Free");
    };
  }, [id, setRenovCredits]); // Depend on 'id' and 'setRenovCredits'

  const handleSaveRoom = async () => {
    setLoading(true);
    try {
      // 1. Update room details (name, superficie)
      const { error: roomUpdateError } = await supabase
        .from("rooms")
        .update({ name, superficie: parseFloat(superficie) })
        .eq("id", room.id);

      if (roomUpdateError) throw roomUpdateError;

      // Get current images from DB to compare
      const { data: existingImages, error: fetchImagesError } = await supabase
        .from("images")
        .select("id, url")
        .eq("room_id", room.id);

      if (fetchImagesError) throw fetchImagesError;

      
      const currentImageIds = new Set(images.filter(img => !img.isNew).map(img => img.id));

      // 2. Delete removed images
      const imagesToDelete = existingImages.filter(img => !currentImageIds.has(img.id));
      for (const img of imagesToDelete) {
        const { error: deleteError } = await supabase
          .from("images")
          .delete()
          .eq("id", img.id);
        if (deleteError) console.error("Error deleting image from DB:", deleteError);
      }

      // 3. Handle new images and update existing ones (default image)
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        if (img.isNew) {
          // New image: upload and insert
          const { error: insertError } = await supabase.from("images").insert([
            {
              url: img.url, // Use the object URL for display/storage
              room_id: room.id,
              is_default: i === defaultImageIndex,
            },
          ]);
          if (insertError) console.error("Error inserting new image:", insertError);
        } else {
          // Existing image: update is_default status if changed
          const { error: updateError } = await supabase
            .from("images")
            .update({ is_default: i === defaultImageIndex })
            .eq("id", img.id);
          if (updateError) console.error("Error updating existing image:", updateError);
        }
      }

      // Re-fetch room details to update the UI with latest data
      const { data: updatedRoomData, error: updatedRoomError } = await supabase
        .from("rooms")
        .select("*, properties(*)")
        .eq("id", id)
        .single();

      if (updatedRoomError) throw updatedRoomError;

      const { data: updatedImagesData, error: updatedImagesError } = await supabase
        .from("images")
        .select("*")
        .eq("room_id", id);

      if (updatedImagesError) throw updatedImagesError;

      setRoom({
        ...updatedRoomData,
        localisation_projet: updatedRoomData?.properties?.departement_id ? (await supabase.from("departements").select("nom").eq("id", updatedRoomData.properties.departement_id).single()).data?.nom : "Non spécifiée",
        surface_habitable_totale_logement_m2: updatedRoomData?.properties?.surface_habitable_totale_logement_m2 || 0,
        age_logement_annees: updatedRoomData?.properties?.age_logement_annees || 0,
      });
      setImages((updatedImagesData || []).map(img => ({ ...img, isNew: false })));

      setIsEditing(false);
      showNotification("Pièce mise à jour avec succès !", "success");
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de la pièce :", error);
      showNotification(`Erreur lors de la sauvegarde : ${error.message || "Erreur inconnue"}`, "danger");
    } finally {
      setLoading(false);
    }
  };

  return {
    room,
    images,
    setImages,
    defaultImageIndex,
    setDefaultImageIndex,
    name,
    setName,
    superficie,
    setSuperficie,
    isEditing,
    setIsEditing,
    handleSaveRoom, // Expose handleSaveRoom
    loading,
    userPlan,
    analyses,
    setAnalyses,
  };
}